/**
 * @file Reads an `orderLinesList` answer into the Orders tab's line rows, in
 * server order, with the URL's lifecycle or past-promised filter applied in
 * the client and the counts the "Showing N of M" caption needs. Consumer:
 * `AnalyticsOrdersPanel`. A line's lifecycle is the first `lineItems` status
 * list holding its status, and a lifecycle filter keeps the lines whose status
 * is in that list, so the model loads until the lists are read. The filter
 * reads only the returned rows, at most the list cap: `returnedLines` counts
 * them before the filter and `totalLines` is the server's uncapped count. A
 * past-promised filter over a refused `pastPromised` column is that column's
 * refusal. `promisedDate` arrives as DuckDB SQL text and is stored as an ISO
 * UTC string; any malformed row fails the whole model with a message.
 */
import { DateTime } from 'luxon';
import type { Snapshot } from 'valtio';

import type { NamedQueryCell, NamedQueryResult } from '#types';

import { analyticsDecimalFraction } from '#types';
import { ANALYTICS_ORDER_LIFECYCLES } from './ANALYTICS_ORDER_LIFECYCLES';
import { analyticsOrderAmount } from './analyticsOrderAmount';
import { analyticsMalformedAnswer } from './analyticsMalformedAnswer';
import { analyticsRefusedColumn } from './analyticsRefusedColumn';
import { analyticsUnansweredFigure } from './analyticsUnansweredFigure';
import type {
  AnalyticsOrderAmount,
  AnalyticsOrderLineRow,
  AnalyticsOrderLines,
} from './analyticsOrdersTab.types';
import type {
  AnalyticsAnswer,
  AnalyticsOrderLifecycle,
  AnalyticsView,
} from './index.types';

type StatusLists = Readonly<
  Partial<Record<AnalyticsOrderLifecycle, readonly string[]>>
>;

const isText = (cell: NamedQueryCell): cell is string | null =>
  cell === null || typeof cell === 'string';

const promisedDateIso = (cell: NamedQueryCell): string | null | undefined => {
  if (cell === null) return null;
  if (typeof cell !== 'string') return undefined;
  const date = DateTime.fromSQL(cell, { setZone: true });
  return date.isValid ? date.toUTC().toISO() : undefined;
};

const lineRow = (
  row: Snapshot<Record<string, NamedQueryCell>>,
  amount: AnalyticsOrderAmount,
  statusLists: StatusLists,
): AnalyticsOrderLineRow | undefined => {
  const { docPath, formNumber, itemType, itemDescription, manufacturerName } =
    row;
  const { quantity, lineItemStatus, pastPromised } = row;
  const value = row[amount.column];
  const promisedDate = promisedDateIso(row.promisedDate);
  const valid =
    typeof docPath === 'string' &&
    isText(formNumber) &&
    isText(itemType) &&
    isText(itemDescription) &&
    isText(manufacturerName) &&
    (quantity === null || typeof quantity === 'number') &&
    isText(lineItemStatus) &&
    (pastPromised === null || typeof pastPromised === 'boolean') &&
    (value === null ||
      (typeof value === 'string' &&
        analyticsDecimalFraction(value) !== undefined)) &&
    promisedDate !== undefined;
  if (!valid) return undefined;
  const lifecycle =
    ANALYTICS_ORDER_LIFECYCLES.find(
      (key) =>
        lineItemStatus !== null &&
        statusLists[key]?.includes(lineItemStatus) === true,
    ) ?? null;
  return {
    docPath,
    formNumber,
    itemType,
    itemDescription,
    manufacturerName,
    quantity,
    lineItemStatus,
    lifecycle,
    value,
    promisedDate,
    pastPromised,
  };
};

const refusedColumns = (
  result: Snapshot<NamedQueryResult>,
  amount: AnalyticsOrderAmount,
) => ({
  value: analyticsRefusedColumn(result, amount.column),
  promisedDate: analyticsRefusedColumn(result, 'promisedDate'),
  itemType: analyticsRefusedColumn(result, 'itemType'),
  itemDescription: analyticsRefusedColumn(result, 'itemDescription'),
  manufacturerName: analyticsRefusedColumn(result, 'manufacturerName'),
  quantity: analyticsRefusedColumn(result, 'quantity'),
});

export const analyticsOrderLines = (
  answer: Snapshot<AnalyticsAnswer> | undefined,
  filter: Pick<AnalyticsView, 'status' | 'pastPromised'>,
  statusLists: StatusLists | undefined,
): AnalyticsOrderLines => {
  if (answer?.status !== 'answered') return analyticsUnansweredFigure(answer);
  if (statusLists === undefined) return { status: 'loading' };
  const { result } = answer;
  const amount = analyticsOrderAmount(result.level);
  const refusedPastPromised = analyticsRefusedColumn(result, 'pastPromised');
  if (filter.pastPromised && refusedPastPromised !== null)
    return { status: 'refusedColumn', column: refusedPastPromised };
  const rows = result.rows.map((row) => lineRow(row, amount, statusLists));
  const parsed = rows.filter(
    (row): row is AnalyticsOrderLineRow => row !== undefined,
  );
  if (parsed.length !== rows.length)
    return analyticsMalformedAnswer('orderLinesList');
  const [first] = result.rows;
  const totalLines = first?.totalLines ?? 0;
  if (typeof totalLines !== 'number' || !Number.isInteger(totalLines))
    return analyticsMalformedAnswer('orderLinesList');
  const filterList = filter.status === null ? null : statusLists[filter.status];
  return {
    status: 'answered',
    rows: parsed.filter(
      (row) =>
        (filter.status === null ||
          (row.lineItemStatus !== null &&
            filterList?.includes(row.lineItemStatus) === true)) &&
        (!filter.pastPromised || row.pastPromised === true),
    ),
    returnedLines: parsed.length,
    totalLines,
    amount,
    refused: refusedColumns(result, amount),
  };
};
